import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Engine telemetry — Section 7 channel #1 (Internal IE dashboard,
 * super_admin only). Hits the IE Worker at
 * `/v1/dashboard/engine-telemetry?window=1h|6h|24h`. Live WAE-backed
 * dashboard for the operator (Jim).
 *
 * Per the IE design lock, this view shows OPERATIONAL signals only —
 * no per-tenant breakdown, no PII, no buyer/bidder identity. Cross-
 * tenant aggregate is the explicit Section 8 layer 2 exception.
 *
 * No chart-library dependency — plain HTML tables + minimal CSS
 * sparkline grids. Recharts (or another lib) can come later as polish
 * without requiring a re-fetch shape change.
 */

type WindowKey = '1h' | '6h' | '24h';

const WINDOWS: readonly { key: WindowKey; label: string }[] = [
  { key: '1h', label: 'Last 1h' },
  { key: '6h', label: 'Last 6h' },
  { key: '24h', label: 'Last 24h' },
];

interface LatencyBuckets {
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  sampleCount: number;
}

interface ThroughputWindow {
  totalCount: number;
  perSecond: number;
}

interface OutcomeBreakdown {
  accepted: number;
  rejected: number;
  acceptedPct: number | null;
}

interface GaugeSnapshot {
  doBufferDepthEvents: number | null;
  oldestUnflushedAgeMs: number | null;
  cfQueueDepthEvents: number | null;
  hyperdrivePoolActive: number | null;
  hyperdrivePoolWaiting: number | null;
  websocketConnectionsActive: number | null;
  websocketDroppedBackpressure: number | null;
  auctionsActive: number | null;
  auctionsClosing: number | null;
  lotsActive: number | null;
  dosHot: number | null;
  dosHibernated: number | null;
  r2CatastrophicWrites: number | null;
}

interface EngineTelemetrySnapshot {
  windowSec: number;
  builtAtMs: number;
  bidLatency: LatencyBuckets;
  bidThroughput: ThroughputWindow;
  bidOutcomes: OutcomeBreakdown;
  workerErrorRatePct: number | null;
  gauges: GaugeSnapshot;
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: EngineTelemetrySnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  // Vite exposes import.meta.env.* — must be prefixed VITE_*.
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function SuperEngineTelemetry() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [windowKey, setWindowKey] = useState<WindowKey>('1h');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message:
          'VITE_INTELLIGENCE_BASE_URL is not configured. Set it in apps/web/.env.local to the IE Worker URL (e.g., https://auctisync-intelligence-staging.shrill-darkness-8417.workers.dev).',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }

    setState({ kind: 'loading' });
    const url = `${base}/v1/dashboard/engine-telemetry?window=${windowKey}`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as EngineTelemetrySnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [windowKey, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '72rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Engine telemetry
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWindowKey(w.key)}
            type="button"
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: windowKey === w.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
              color: windowKey === w.key ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {w.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading telemetry…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Telemetry unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <SnapshotView snapshot={state.data} />
      )}
    </main>
  );
}

function SnapshotView({ snapshot }: { snapshot: EngineTelemetrySnapshot }) {
  const builtAt = new Date(snapshot.builtAtMs).toISOString().replace('T', ' ').slice(0, 19);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
          gap: '0.75rem',
        }}
      >
        <KPI
          label="Latency p50"
          value={fmtMs(snapshot.bidLatency.p50Ms)}
          sub={`${snapshot.bidLatency.sampleCount} samples`}
        />
        <KPI label="Latency p95" value={fmtMs(snapshot.bidLatency.p95Ms)} />
        <KPI label="Latency p99" value={fmtMs(snapshot.bidLatency.p99Ms)} accent />
        <KPI
          label="Bid throughput"
          value={`${snapshot.bidThroughput.totalCount.toLocaleString()}`}
          sub={`${snapshot.bidThroughput.perSecond.toFixed(3)} /s`}
        />
        <KPI label="Acceptance rate" value={fmtPct(snapshot.bidOutcomes.acceptedPct)} />
        <KPI
          label="Worker error rate"
          value={fmtPct(snapshot.workerErrorRatePct)}
          accent={
            snapshot.workerErrorRatePct !== null && snapshot.workerErrorRatePct > 1 ? true : false
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bid outcomes</CardTitle>
          <CardDescription>Accepted vs rejected over the window.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <thead>
              <tr>
                <Th>Outcome</Th>
                <Th align="right">Count</Th>
                <Th align="right">Share</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>Accepted</Td>
                <Td align="right">{snapshot.bidOutcomes.accepted.toLocaleString()}</Td>
                <Td align="right">{fmtPct(snapshot.bidOutcomes.acceptedPct)}</Td>
              </tr>
              <tr>
                <Td>Rejected</Td>
                <Td align="right">{snapshot.bidOutcomes.rejected.toLocaleString()}</Td>
                <Td align="right">
                  {fmtPct(
                    snapshot.bidOutcomes.acceptedPct === null
                      ? null
                      : 100 - snapshot.bidOutcomes.acceptedPct,
                  )}
                </Td>
              </tr>
            </tbody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gauges (last value in window)</CardTitle>
          <CardDescription>
            Cross-tenant aggregate. Snapshot taken at {builtAt}Z over a{' '}
            {windowLabel(snapshot.windowSec)} window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <thead>
              <tr>
                <Th>Metric</Th>
                <Th align="right">Value</Th>
              </tr>
            </thead>
            <tbody>
              <GaugeRow
                label="DO buffer depth (events)"
                value={snapshot.gauges.doBufferDepthEvents}
              />
              <GaugeRow
                label="Oldest unflushed event (ms)"
                value={snapshot.gauges.oldestUnflushedAgeMs}
              />
              <GaugeRow
                label="CF Queue depth (events)"
                value={snapshot.gauges.cfQueueDepthEvents}
              />
              <GaugeRow
                label="Hyperdrive pool — active"
                value={snapshot.gauges.hyperdrivePoolActive}
              />
              <GaugeRow
                label="Hyperdrive pool — waiting"
                value={snapshot.gauges.hyperdrivePoolWaiting}
                warn={
                  snapshot.gauges.hyperdrivePoolWaiting !== null &&
                  snapshot.gauges.hyperdrivePoolWaiting > 0
                }
              />
              <GaugeRow
                label="WebSocket connections active"
                value={snapshot.gauges.websocketConnectionsActive}
              />
              <GaugeRow
                label="WS clients dropped (backpressure)"
                value={snapshot.gauges.websocketDroppedBackpressure}
                warn={
                  snapshot.gauges.websocketDroppedBackpressure !== null &&
                  snapshot.gauges.websocketDroppedBackpressure > 0
                }
              />
              <GaugeRow label="Auctions active" value={snapshot.gauges.auctionsActive} />
              <GaugeRow label="Auctions closing" value={snapshot.gauges.auctionsClosing} />
              <GaugeRow label="Lots active" value={snapshot.gauges.lotsActive} />
              <GaugeRow label="DOs hot" value={snapshot.gauges.dosHot} />
              <GaugeRow label="DOs hibernated" value={snapshot.gauges.dosHibernated} />
              <GaugeRow
                label="R2 catastrophic writes"
                value={snapshot.gauges.r2CatastrophicWrites}
                warn={
                  snapshot.gauges.r2CatastrophicWrites !== null &&
                  snapshot.gauges.r2CatastrophicWrites > 0
                }
              />
            </tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--surface-elev)',
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '0.85rem 1rem',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: '0.3rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: accent ? 'var(--semantic-warning)' : 'var(--text)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub !== undefined && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            marginTop: '0.3rem',
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function Table({ children }: { children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
        }}
      >
        {children}
      </table>
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: '0.5rem 0.6rem',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text-muted)',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: '0.45rem 0.6rem',
        borderBottom: '1px solid var(--border-subtle, var(--border))',
        color: 'var(--text)',
      }}
    >
      {children}
    </td>
  );
}

function GaugeRow({ label, value, warn }: { label: string; value: number | null; warn?: boolean }) {
  return (
    <tr>
      <Td>{label}</Td>
      <Td align="right">
        <span
          style={{
            color: warn ? 'var(--semantic-warning)' : undefined,
            fontWeight: warn ? 700 : 400,
          }}
        >
          {value === null ? '—' : value.toLocaleString()}
        </span>
      </Td>
    </tr>
  );
}

function fmtMs(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(value < 10 ? 1 : 0)} ms`;
}

function fmtPct(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(value < 10 ? 2 : 1)}%`;
}

function windowLabel(sec: number): string {
  if (sec === 3600) return '1-hour';
  if (sec === 21600) return '6-hour';
  if (sec === 86400) return '24-hour';
  return `${sec}-second`;
}
